import AddIcon from '@mui/icons-material/Add';
import { SelectChangeEvent, Stack, Typography } from '@mui/material';

import { TemplateComponentButtonType, WhatsAppButton } from 'domains/content/types';
import Select from 'domains/core/components/Select';

import { getButtonValidationErrors } from '../../utils/whatsAppUtils';

import ButtonItem from './ButtonItem';

const MAX_BUTTONS = 10;

const DEFAULT_NEW_BUTTON: Record<TemplateComponentButtonType, () => WhatsAppButton> = {
    [TemplateComponentButtonType.QUICK_REPLY]: () => ({ type: TemplateComponentButtonType.QUICK_REPLY, text: '' }),
    [TemplateComponentButtonType.URL]: () => ({ type: TemplateComponentButtonType.URL, text: '', url: '' }),
    [TemplateComponentButtonType.PHONE_NUMBER]: () => ({
        type: TemplateComponentButtonType.PHONE_NUMBER,
        text: '',
        phone_number: '',
    }),
    [TemplateComponentButtonType.COPY_CODE]: () => ({ type: TemplateComponentButtonType.COPY_CODE }),
    [TemplateComponentButtonType.COPY_OFFER_CODE]: () => ({
        type: TemplateComponentButtonType.COPY_OFFER_CODE,
        copy_code: '',
    }),
    [TemplateComponentButtonType.FLOW]: () => ({ type: TemplateComponentButtonType.FLOW, text: '', flow_id: '' }),
};

export type Props = {
    buttons: WhatsAppButton[];
    onAdd: (button: WhatsAppButton) => void;
    onUpdate: (index: number, button: WhatsAppButton) => void;
    onRemove: (index: number) => void;
    onReorder: (buttons: WhatsAppButton[]) => void;
};

const WhatsAppButtonsSection = ({ buttons, onAdd, onUpdate, onRemove }: Props) => {
    const errors = getButtonValidationErrors(buttons);
    const atMax = buttons.length >= MAX_BUTTONS;

    const handleAddType = (e: SelectChangeEvent<unknown>) => {
        const buttonType = e.target.value as TemplateComponentButtonType;
        onAdd(DEFAULT_NEW_BUTTON[buttonType]());
        // Reset select value by using a key-controlled component isn't easy here;
        // the parent controls state so the select value won't persist after add
    };

    return (
        <Stack spacing={2} data-testid="whatsapp-buttons-section">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" fontWeight={600}>
                    Buttons{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                        (optional · max {MAX_BUTTONS})
                    </Typography>
                </Typography>
            </Stack>

            {buttons.map((button, index) => (
                <ButtonItem
                    key={index}
                    button={button}
                    index={index}
                    error={errors[index]}
                    onChange={onUpdate}
                    onRemove={onRemove}
                />
            ))}

            <Select
                data-testid="add-button-select"
                value=""
                onChange={handleAddType}
                displayEmpty
                disabled={atMax}
                size="small"
                fullWidth={false}
                sx={{ minWidth: 200 }}
                inputProps={{ 'aria-label': 'Add button type' }}
                renderValue={() => (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AddIcon fontSize="small" />
                        <span>{atMax ? `Maximum ${MAX_BUTTONS} buttons` : 'Add button'}</span>
                    </Stack>
                )}
                values={[
                    { value: TemplateComponentButtonType.QUICK_REPLY, label: 'Quick Reply' },
                    { value: TemplateComponentButtonType.URL, label: 'URL' },
                    { value: TemplateComponentButtonType.PHONE_NUMBER, label: 'Phone Number' },
                    { value: TemplateComponentButtonType.COPY_CODE, label: 'OTP (Copy Code)' },
                    { value: TemplateComponentButtonType.COPY_OFFER_CODE, label: 'Copy Offer Code' },
                    { value: TemplateComponentButtonType.FLOW, label: 'Flow' },
                ]}
            />
        </Stack>
    );
};

export default WhatsAppButtonsSection;
